import { useCallback, useMemo } from 'react';
import { useTranslation } from '../contexts/TranslationContext';

const useTranslate = () => {
  const { currentLanguage, translationCache, isLoading, translateNewContent } = useTranslation();

  // Generate cache key with hash for long texts (same logic as context)
  const getCacheKey = useCallback((text, language) => {
    if (text.length > 100) {
      const hash = text.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return `${language}_${Math.abs(hash)}`;
    }
    return `${text}_${language}`;
  }, []);

  // Basic translate function
  const translate = useCallback((text, options = {}) => {
    // Handle null/undefined
    if (!text) return text;
    
    // If it's English, return the original text
    if (currentLanguage === 'en') {
      return text;
    }

    // Check cache for translation
    const cacheKey = getCacheKey(text, currentLanguage);
    const cached = translationCache[cacheKey];
    
    if (cached) {
      return cached;
    }

    // If fallback is provided and not cached, use fallback
    if (options.fallback && options.fallback !== text) {
      const fallbackKey = getCacheKey(options.fallback, currentLanguage);
      const fallbackCached = translationCache[fallbackKey];
      if (fallbackCached) {
        return fallbackCached;
      }
    }

    // If not cached, return original text
    // The translation context will handle translating it when it scans the DOM
    return text;
  }, [currentLanguage, translationCache, getCacheKey]);

  // Translate with interpolation (for dynamic content)
  const translateWithVars = useCallback((text, variables = {}) => {
    let translatedText = translate(text);
    
    // Replace variables in the format {{variable}}
    Object.keys(variables).forEach(key => {
      const placeholder = `{{${key}}}`;
      translatedText = translatedText.replace(new RegExp(placeholder, 'g'), variables[key]);
    });
    
    return translatedText;
  }, [translate]);

  // Get translation for form elements (handles placeholders, labels, etc.)
  const translateForm = useCallback((formTexts) => {
    const translated = {};
    
    Object.keys(formTexts).forEach(key => {
      translated[key] = translate(formTexts[key]);
    });
    
    return translated;
  }, [translate]);

  // Translate array of strings
  const translateArray = useCallback((textArray) => {
    return textArray.map(text => translate(text));
  }, [translate]);

  // Check if translation exists in cache
  const hasTranslation = useCallback((text) => {
    if (currentLanguage === 'en') return true;
    const cacheKey = getCacheKey(text, currentLanguage);
    return !!translationCache[cacheKey];
  }, [currentLanguage, translationCache, getCacheKey]);

  // Get translation status for debugging
  const getTranslationStatus = useMemo(() => ({
    currentLanguage,
    isTranslating: currentLanguage !== 'en',
    isLoading,
    cacheSize: Object.keys(translationCache).length,
    hasCache: Object.keys(translationCache).length > 0
  }), [currentLanguage, isLoading, translationCache]);

  // Batch translate multiple texts (for component initialization)
  const translateBatch = useCallback((texts) => {
    if (!Array.isArray(texts)) {
      console.warn('translateBatch expects an array of texts');
      return {};
    }

    const translations = {};
    texts.forEach((text, index) => {
      const key = typeof text === 'object' ? text.key : `item_${index}`;
      const value = typeof text === 'object' ? text.value : text;
      translations[key] = translate(value);
    });

    return translations;
  }, [translate]);

  // Mark element as untranslatable (adds data attribute)
  const markUntranslatable = useCallback((ref) => {
    if (ref && ref.current) {
      ref.current.setAttribute('data-no-translate', 'true');
    }
  }, []);

  // Trigger translation for new content (for API-loaded content)
  const triggerTranslation = useCallback(async (selector = null) => {
    if (translateNewContent) {
      await translateNewContent(selector);
    }
  }, [translateNewContent]);

  return {
    // Core functions
    translate,
    translateWithVars,
    translateForm,
    translateArray,
    translateBatch,
    
    // Utility functions
    hasTranslation,
    markUntranslatable,
    triggerTranslation,
    
    // Status
    ...getTranslationStatus,
    
    // Aliases for convenience
    t: translate,
    tv: translateWithVars,
    tf: translateForm,
    ta: translateArray,
    tt: triggerTranslation
  };
};

export default useTranslate; 